/**
 * Auth Context Provider
 *
 * REUSABLE: Copy this file to other Gatsby projects
 * Provides authentication state and methods throughout the app
 */

import React, { createContext, useContext, useState, useEffect, useRef } from 'react'
import { navigate } from 'gatsby'
import { supabase } from './supabaseClient'

const AuthContext = createContext(null)

// Debounce login event recording — Supabase fires SIGNED_IN twice per login.
// Only record one event per user per 10-second window.
const recentLoginTimestamps = new Map()

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(true)
  const presenceChannelRef = useRef(null)

  const joinPresence = (userId) => {
    if (presenceChannelRef.current) supabase.removeChannel(presenceChannelRef.current)
    const ch = supabase.channel('mb24-user-presence')
    ch.subscribe(async (status) => {
      if (status === 'SUBSCRIBED') {
        await ch.track({ user_id: userId })
        supabase.from('profiles').update({ last_seen_at: new Date().toISOString() }).eq('id', userId).then(() => {})
      }
    })
    presenceChannelRef.current = ch
  }

  const leavePresence = () => {
    if (presenceChannelRef.current) {
      supabase.removeChannel(presenceChannelRef.current)
      presenceChannelRef.current = null
    }
  }

  // Fetch user profile from profiles table
  const fetchProfile = async (userId) => {
    try {
      const { data, error } = await supabase.from('profiles').select('*').eq('id', userId).single()

      if (error) {
        console.error('Error fetching profile:', error)
        return null
      }
      return data
    } catch (error) {
      console.error('Error in fetchProfile:', error)
      return null
    }
  }

  // Update user profile
  const updateProfile = async (updates) => {
    if (!user) return { error: { message: 'No user logged in' } }

    try {
      const { data, error } = await supabase.from('profiles').upsert({ id: user.id, ...updates, updated_at: new Date().toISOString() }).select().single()

      if (error) throw error

      setProfile(data)
      return { data, error: null }
    } catch (error) {
      console.error('Error updating profile:', error)
      return { data: null, error }
    }
  }

  // Sign up with email and password
  const signUp = async (email, password, metadata = {}, redirectTo = null, captchaToken = null) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: metadata,
          ...(redirectTo && { emailRedirectTo: redirectTo }),
          ...(captchaToken && { captchaToken })
        }
      })
      return { data, error }
    } catch (error) {
      return { data: null, error }
    }
  }

  // Sign in with email and password
  const signIn = async (email, password, captchaToken = null) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
        options: {
          ...(captchaToken && { captchaToken })
        }
      })
      return { data, error }
    } catch (error) {
      return { data: null, error }
    }
  }

  // Sign in with OAuth provider (Google, GitHub, etc.)
  const signInWithOAuth = async (provider, options = {}) => {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          ...options
        }
      })
      return { data, error }
    } catch (error) {
      return { data: null, error }
    }
  }

  // Sign out
  const signOut = () => {
    // Clear all auth storage SYNCHRONOUSLY first — before any async Supabase
    // operations (auto-refresh timer, onAuthStateChange) can race to re-write it
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith('sb-') || key.startsWith('supabase') || key === 'supabase_access_token') {
        localStorage.removeItem(key)
      }
    })

    leavePresence()

    // Clear React state
    setUser(null)
    setProfile(null)
    setSession(null)

    // Notify other tabs immediately
    try {
      const bc = new BroadcastChannel('mb24_auth')
      bc.postMessage({ type: 'SIGNED_OUT' })
      bc.close()
    } catch {}

    // Fire-and-forget server invalidation — non-blocking, failure is acceptable
    // because the local session is already gone
    supabase.auth.signOut().catch(() => {})

    return { error: null }
  }

  // Reset password
  const resetPassword = async (email, captchaToken = null) => {
    try {
      const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`,
        ...(captchaToken && { captchaToken })
      })
      return { data, error }
    } catch (error) {
      return { data: null, error }
    }
  }

  // Update password
  const updatePassword = async (newPassword) => {
    try {
      const { data, error } = await supabase.auth.updateUser({
        password: newPassword
      })
      return { data, error }
    } catch (error) {
      return { data: null, error }
    }
  }

  // Get current access token (useful for BidJS integration)
  const getAccessToken = () => {
    return session?.access_token || null
  }

  useEffect(() => {
    // Get initial session
    const initializeAuth = async () => {
      try {
        const {
          data: { session: currentSession }
        } = await supabase.auth.getSession()

        setSession(currentSession)
        setUser(currentSession?.user || null)

        if (currentSession?.user) {
          const userProfile = await fetchProfile(currentSession.user.id)
          setProfile(userProfile)
          joinPresence(currentSession.user.id)
        }
      } catch (error) {
        if (error?.name !== 'AbortError') {
          console.error('Error initializing auth:', error)
        }
      } finally {
        setLoading(false)
      }
    }

    initializeAuth()

    // Listen for auth changes
    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange(async (event, newSession) => {
      setSession(newSession)
      setUser(newSession?.user || null)

      if (newSession?.user) {
        const userProfile = await fetchProfile(newSession.user.id)
        setProfile(userProfile)
        joinPresence(newSession.user.id)
      } else {
        setProfile(null)
        leavePresence()
      }

      // Record login event (debounced — SIGNED_IN fires twice per login)
      if (event === 'SIGNED_IN' && newSession?.user) {
        const userId = newSession.user.id
        const now = Date.now()
        const last = recentLoginTimestamps.get(userId) || 0
        if (now - last > 10000) {
          recentLoginTimestamps.set(userId, now)
          supabase
            .from('login_events')
            .insert({ user_id: userId })
            .then(({ error }) => {
              if (error) console.error('login_events insert failed:', error)
            })
        }
      }

      // Store token for BidJS if needed
      if (newSession?.access_token) {
        localStorage.setItem('supabase_access_token', newSession.access_token)
      } else {
        localStorage.removeItem('supabase_access_token')
      }
    })

    // Cross-tab sign-out: BroadcastChannel (primary) + storage event (fallback)
    const handleRemoteSignOut = () => {
      setUser(null)
      setProfile(null)
      setSession(null)
      localStorage.removeItem('supabase_access_token')
      navigate('/auth/login')
    }

    let bc
    try {
      bc = new BroadcastChannel('mb24_auth')
      bc.onmessage = (e) => { if (e.data?.type === 'SIGNED_OUT') handleRemoteSignOut() }
    } catch {}

    // Storage event fires in every tab *except* the one that made the change —
    // catches signouts even if BroadcastChannel isn't supported
    const handleStorage = (e) => {
      if (e.key && e.key.includes('-auth-token') && e.newValue === null) {
        handleRemoteSignOut()
      }
    }
    window.addEventListener('storage', handleStorage)

    return () => {
      subscription?.unsubscribe()
      bc?.close()
      window.removeEventListener('storage', handleStorage)
      leavePresence()
    }
  }, [])

  const value = {
    user,
    profile,
    session,
    loading,
    signUp,
    signIn,
    signInWithOAuth,
    signOut,
    resetPassword,
    updatePassword,
    updateProfile,
    getAccessToken,
    isAuthenticated: !!user,
    isAdmin: profile?.is_admin === true,
    isApproved: profile?.is_approved === true,
    isBuyer: profile?.role === 'buyer',
    isSeller: profile?.role === 'seller'
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export default AuthContext
